import React, { useEffect, useRef } from 'react';
import { Spin } from 'antd';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import getEnv from '../env';
import RegistrationStatusScreen from './RegistrationStatusScreen';
import ConsentScreen from './ConsentScreen';
import OnboardingScreen from './Onboarding/OnboardingScreen';
import { usePendingConsents } from '../hooks/use_pending_consents';

const API_URL = getEnv('REACT_APP_API_URL') || 'http://localhost:8000';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, login, registrationStatus, user } = useAuth();
  // Pending users can already answer consents during onboarding; only
  // rejected users (no account access at all) skip the fetch.
  const consentUserId = isAuthenticated && registrationStatus !== 'rejected'
    ? user?.id
    : null;
  const { pending: pendingConsents, loading: consentsLoading, submit: submitConsents } = usePendingConsents(consentUserId);
  const location = useLocation();
  const beaconSent = useRef(false);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !beaconSent.current) {
      beaconSent.current = true;
      fetch(`${API_URL}/api/analytics/pre-auth-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: location.pathname }),
      }).catch(() => {});
    }
  }, [isLoading, isAuthenticated, location.pathname]);

  if (isLoading || (isAuthenticated && registrationStatus === null) || consentsLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    login();
    return null;
  }

  if (registrationStatus === 'rejected') {
    return <RegistrationStatusScreen status={registrationStatus} />;
  }

  if (registrationStatus === 'pending') {
    return (
      <OnboardingScreen
        consents={pendingConsents}
        onSubmitConsents={submitConsents}
      />
    );
  }

  // Unanswered consents (new, or re-worded since they last answered) come
  // before the app itself.
  if (pendingConsents.length > 0) {
    return <ConsentScreen consents={pendingConsents} onSubmit={submitConsents} />;
  }

  return children;
};

export default ProtectedRoute;
